import { useCallback, useEffect, useRef, useState } from "react";
import { Bell, CheckCheck, ExternalLink, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TARGETS = {
  client_evaluation_request: { client: "/client-evaluation-request", provider: "/provider-portal", default: "/evaluation-pipeline" },
  request: { client: "/staffing-requests", default: "/master-staffing-board" },
  assignment: { provider: "/provider-portal", default: "/master-staffing-board" },
  weekly_digest: { default: "/evaluation-pipeline" },
};

function timeAgo(value) {
  const diff = Date.now() - new Date(value).getTime();
  const minutes = Math.round(diff / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function InlineReply({ item, onDone, onOpen }) {
  const [reply, setReply] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  async function send(event) {
    event.preventDefault();
    setSending(true);
    setError("");
    try {
      await api.post(`/client-evaluation-requests/${item.entity_id}/respond`, { response: reply });
      toast.success("Reply sent to TAG");
      onDone();
    } catch (requestError) {
      setError(
        requestError?.response?.status === 400
          ? "This request has already been answered or closed. Open the request to see its current status."
          : formatApiError(requestError),
      );
    } finally {
      setSending(false);
    }
  }

  return (
    <form className="notification-reply" data-testid={`notification-reply-${item.id}`} onSubmit={send}>
      <blockquote>{item.body}</blockquote>
      <textarea
        autoFocus
        data-testid={`notification-reply-input-${item.id}`}
        placeholder="Type your reply to TAG…"
        rows="3"
        value={reply}
        onChange={(event) => setReply(event.target.value)}
      />
      {error && <p className="form-message error-message" data-testid={`notification-reply-error-${item.id}`}>{error}</p>}
      <div className="notification-reply-actions">
        <button className="notification-reply-link" data-testid={`notification-reply-open-${item.id}`} type="button" onClick={onOpen}>
          <ExternalLink size={13} /> Open request
        </button>
        <button className="button button-primary" data-testid={`notification-reply-send-${item.id}`} disabled={sending || reply.trim().length < 2} type="submit">
          <Send size={13} /> {sending ? "Sending…" : "Send reply"}
        </button>
      </div>
    </form>
  );
}

export const NotificationBell = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const wrapRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const response = await api.get("/notifications", { params: { limit: 30 } });
      setItems(response.data.items);
      setUnread(response.data.unread);
    } catch {
      /* header stays usable without notifications */
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  function markRead(item) {
    if (item.read_at) return;
    setItems((current) => current.map((entry) => (entry.id === item.id ? { ...entry, read_at: new Date().toISOString() } : entry)));
    setUnread((count) => Math.max(0, count - 1));
    api.post(`/notifications/${item.id}/read`).catch(() => {});
  }

  function goTo(item) {
    const target = TARGETS[item.entity_type];
    if (target) {
      setOpen(false);
      setReplyingTo(null);
      if (user.role === "provider" && item.entity_type === "client_evaluation_request" && item.entity_id) {
        navigate(`/provider-portal/work/${item.entity_id}`);
        return;
      }
      navigate(target[user.role] || target.default);
    }
  }

  function openItem(item) {
    const canReplyInline = item.type === "evaluation_info_request" && user.role === "client";
    if (canReplyInline) {
      setReplyingTo((current) => (current === item.id ? null : item.id));
      return;
    }
    markRead(item);
    goTo(item);
  }

  async function readAll() {
    await api.post("/notifications/read-all").catch(() => {});
    setItems((current) => current.map((entry) => ({ ...entry, read_at: entry.read_at || new Date().toISOString() })));
    setUnread(0);
  }

  return (
    <div className="notification-wrap" ref={wrapRef}>
      <button
        className="icon-button notification-button"
        type="button"
        aria-label="Open notifications"
        aria-expanded={open}
        data-testid="notifications-button"
        onClick={() => setOpen((value) => !value)}
      >
        <Bell size={19} />
        {unread > 0 && <span data-testid="notification-count">{unread > 99 ? "99+" : unread}</span>}
      </button>
      {open && (
        <div className="notification-panel" data-testid="notification-panel">
          <div className="notification-panel-heading">
            <strong>Notifications</strong>
            <button className="notification-read-all" data-testid="notifications-read-all" disabled={!unread} type="button" onClick={readAll}>
              <CheckCheck size={14} /> Mark all read
            </button>
          </div>
          <div className="notification-list">
            {items.map((item) => (
              <div className={`notification-entry ${replyingTo === item.id ? "is-replying" : ""}`} key={item.id}>
                <button
                  className={`notification-item ${item.read_at ? "" : "is-unread"} notification-${item.type}`}
                  data-testid={`notification-item-${item.id}`}
                  type="button"
                  onClick={() => openItem(item)}
                >
                  <span className="notification-dot" aria-hidden="true" />
                  <span className="notification-copy">
                    <strong>{item.title}</strong>
                    <small>{item.body}</small>
                    <em>
                      {timeAgo(item.created_at)}
                      {item.type === "evaluation_info_request" && user.role === "client" && <b className="notification-reply-tag"> · Reply here</b>}
                    </em>
                  </span>
                </button>
                {replyingTo === item.id && (
                  <InlineReply
                    item={item}
                    onDone={() => {
                      markRead(item);
                      setReplyingTo(null);
                    }}
                    onOpen={() => {
                      markRead(item);
                      goTo(item);
                    }}
                  />
                )}
              </div>
            ))}
            {!items.length && <p className="notification-empty" data-testid="notifications-empty">You're all caught up.</p>}
          </div>
        </div>
      )}
    </div>
  );
};
